import React, { createContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Container,
  Grid,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import { useNavigate } from "react-router-dom";
import PaymentForms from "../../paymentPage";
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";
import {
  useCreatePaymentMutation,
  usePaymentsQuery,
} from "../../LoginPage/store/loginSlice";
import { useSelector } from "react-redux";

// Estilos personalizados
const GradientTypography = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  color: "transparent",
}));

const StyledCard = styled(Card)(({ theme, ispopular }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  transition: "all 0.3s ease-in-out",
  border:
    ispopular === "true"
      ? `2px solid ${theme.palette.primary.main}`
      : "1px solid rgba(0, 0, 0, 0.12)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: theme.shadows[10],
  },
}));

export const CardContext = createContext(null);
const PricingPlans = () => {
  const itsauth = localStorage.getItem("hasActivePlan");
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [createPayment, result] = useCreatePaymentMutation();
  const { token, whoami } = useSelector((state) => state.auth);
  const { data: payments = [], refetch: refetchPayments } = usePaymentsQuery({
    token,
    userId: whoami?.id,
  });

  const [cardInfo, setCardInfo] = useState({
    number: "",
    name: "",
    expiry: "",
    cvc: "",
    focus: "",
  });

  // Determina si hay un pago activo en este momento
  const activePayment = payments.find(
    (payment) => new Date(payment.expiration_date) > new Date()
  );

  // Nuevos planes de suscripción
  const plans = [
    {
      id: 1,
      name: "1 Mes",
      price: "$9.99",
      description: "Suscripción mensual",
      isPopular: false,
    },
    {
      id: 2,
      name: "3 Meses",
      price: "$28.99",
      description: "Suscripción trimestral",
      isPopular: false,
    },
    {
      id: 3,
      name: "6 Meses",
      price: "$57.99",
      description: "Suscripción semestral",
      isPopular: false,
    },
    {
      id: 4,
      name: "1 Año",
      price: "$115.99",
      description: "Suscripción anual",
      isPopular: true,
    },
  ];

  const [dialogOptions, setDialogOptions] = useState({
    open: false,
    planId: null,
  });
  const setOpen = (open) => setDialogOptions({ ...dialogOptions, open });

  useEffect(() => {
    const comingFromMenu = localStorage.getItem("comingFromMenu");
    if (!comingFromMenu) {
      const timeout = setTimeout(() => {
        setLoading(false);
      }, 700);
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, []);
  

  useEffect(() => {
    const shouldRedirect = sessionStorage.getItem("justLoggedIn");
    if (shouldRedirect && itsauth === "true") {
      sessionStorage.removeItem("justLoggedIn");
      window.location.href = "/menu";
    }
  }, []);
  useEffect(() => {
    if (result.isLoading) {
      Swal.fire({
        title: "Procesando pago...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    } else if (result.isSuccess) {
      Swal.fire({
        icon: "success",
        title: "Pago completado",
        text:
          "Su pago ha sido procesado exitosamente. Referencia: " +
          result.data.reference,
      });
      refetchPayments();
    } else if (result.isError) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          "Hubo un problema procesando su pago.\n\n" +
          result.error.data?.detail,
      });
    }
  }, [
    result.isLoading,
    result.isSuccess,
    result.isError,
    result.data,
    result.error,
    refetchPayments,
  ]);
  const handlePayment = async () => {
    const today = new Date();
    let monthsToAdd = 1;
    let amount = "000000000999";
    switch (dialogOptions.planId) {
      case 1:
        monthsToAdd = 1;
        amount = "000000000999";
        break;
      case 2:
        monthsToAdd = 3;
        amount = "000000002899";
        break;
      case 3:
        monthsToAdd = 6;
        amount = "000000005799";
        break;
      case 4:
        monthsToAdd = 12;
        amount = "000000011599";
        break;
      default:
        monthsToAdd = 1;
        amount = "000000000999";
        break;
    }

    const expirationDate = new Date();
    expirationDate.setMonth(today.getMonth() + monthsToAdd);

    const transactionId = uuidv4();

    // Invierte MMYY a YYMM
    let cardExpiry = cardInfo.expiry;
    if (cardExpiry && cardExpiry.length === 4) {
      cardExpiry = cardExpiry.slice(2, 4) + cardExpiry.slice(0, 2);
    }

    const paymentPayload = {
      token,
      payment: {
        card_number: cardInfo.number,
        card_expiration: cardExpiry, // ahora en formato YYMM
        card_ccv: cardInfo.cvc,
        amount_12_digit: amount,
        user_id: whoami.id,
        amount: 0,
        payment_method: "string",
        status: "string",
        transaction_id: transactionId,
        expiration_date: expirationDate.toISOString(),
        credit_type: "string",
        installments: dialogOptions.planId,
        interest: 0,
      },
    };


    setOpen(false);
    createPayment(paymentPayload);
  };

  useEffect(() => {
    // Check localStorage flag for active plan
    const hasActivePlanFlag = localStorage.getItem("hasActivePlan") === "true";

    // Only redirect if NOT coming from menu (no flag in localStorage)
    if (!localStorage.getItem("comingFromMenu")) {
      if (hasActivePlanFlag) {
        // If user has an active plan, redirect to menu
        navigate("/menu");
      }
    }
  }, [payments, navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <CircularProgress size={80} thickness={4} />
      </Box>
    );
  }


  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 4,
        background: `linear-gradient(to bottom right, ${theme.palette.grey[50]}, ${theme.palette.grey[100]})`,
      }}
    >
      <CardContext.Provider value={{ cardInfo, setCardInfo }}>
        <PaymentForms
          open={dialogOptions.open}
          onClose={() => {
            // Reset card info when closing the dialog
            setCardInfo({
              number: "",
              name: "",
              expiry: "",
              cvc: "",
              focus: "",
            });
            setOpen(false);
          }}
          handlePayment={handlePayment}
        />
      </CardContext.Provider>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box textAlign="center" mb={8}>
          <GradientTypography
            variant="h3"
            component="h2"
            gutterBottom
            fontWeight="bold"
          >
            ESTE ES EL SALARIO DE SU ASISTENTE
          </GradientTypography>
          <Typography variant="h5" color="text.secondary">
            POR CUANTO TIEMPO DESEA CONTRATARLO
          </Typography>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={4} justifyContent="center">
          {plans
            .filter(
              (plan) => !activePayment || plan.id === activePayment.installments
            ) // Filtra solo después de obtener los pagos
            .map((plan) => (
              <Grid item xs={12} md={6} lg={3} key={plan.id}>
                <StyledCard ispopular={plan.isPopular.toString()}>
                  {plan.isPopular && (
                    <Chip
                      label="Popular"
                      color="secondary"
                      sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                      }}
                    />
                  )}
                  {activePayment && activePayment.installments === plan.id && (
                    <Chip
                      label="Plan actual"
                      color="primary"
                      sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                      }}
                    />
                  )}

                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      fontWeight="bold"
                    >
                      {plan.name}
                    </Typography>
                    <Typography color="text.secondary" gutterBottom>
                      {plan.description}
                    </Typography>

                    <Box sx={{ my: 3 }}>
                      <Typography
                        variant="h4"
                        component="span"
                        fontWeight="bold"
                      >
                        {plan.price}
                      </Typography>
                    </Box>
                  </CardContent>

                  {!activePayment && (
                    <CardActions sx={{ p: 4, pt: 0 }}>
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={() =>
                          setDialogOptions({ open: true, planId: plan.id })
                        }
                        sx={{
                          py: 1.5,
                          backgroundColor: plan.isPopular
                            ? "primary.main"
                            : "grey.800",
                          "&:hover": {
                            backgroundColor: plan.isPopular
                              ? "primary.dark"
                              : "grey.900",
                          },
                        }}
                      >
                        Seleccionar
                      </Button>
                    </CardActions>
                  )}
                </StyledCard>
              </Grid>
            ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default PricingPlans;
